import { useState, useEffect } from 'react';
import { X, Share, Plus, Check } from 'lucide-react';
import { Button } from '@/components/ui/button';

const IOSInstallPopup = () => {
  const [showPopup, setShowPopup] = useState(false);
  const [showInstructions, setShowInstructions] = useState(false);

  useEffect(() => {
    // Check if user is on iOS Safari and NOT already installed as PWA
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
    const isInStandaloneMode = window.navigator.standalone === true;
    const isSafari = /Safari/.test(navigator.userAgent) && !/Chrome/.test(navigator.userAgent);
    
    // Check if user has dismissed the popup before
    const dismissed = localStorage.getItem('ios-install-popup-dismissed');
    const dismissedDate = dismissed ? new Date(dismissed) : null;
    const daysSinceDismissed = dismissedDate 
      ? (new Date() - dismissedDate) / (1000 * 60 * 60 * 24) 
      : null;
    
    // Show popup if:
    // - User is on iOS
    // - Not already installed as PWA
    // - Using Safari (PWA install only works in Safari)
    // - Hasn't dismissed in the last 7 days
    if (isIOS && !isInStandaloneMode && isSafari && (!daysSinceDismissed || daysSinceDismissed > 7)) {
      // Delay showing popup by 2 seconds so user can see the page first
      const timer = setTimeout(() => {
        setShowPopup(true);
      }, 2000);
      return () => clearTimeout(timer);
    }
  }, []);

  const handleDismiss = () => {
    localStorage.setItem('ios-install-popup-dismissed', new Date().toISOString());
    setShowPopup(false);
  };

  const handleShowInstructions = () => {
    setShowInstructions(true);
  };

  if (!showPopup) return null;

  return (
    <div className="fixed inset-0 z-[9999] flex items-end sm:items-center justify-center">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        onClick={handleDismiss}
      />
      
      {/* Popup */}
      <div className="relative bg-white rounded-t-3xl sm:rounded-3xl w-full sm:max-w-md mx-auto p-6 pb-8 sm:m-4 shadow-2xl animate-in slide-in-from-bottom duration-300">
        {/* Close button */}
        <button 
          onClick={handleDismiss}
          className="absolute top-4 right-4 text-gray-400 hover:text-gray-600"
        >
          <X className="w-6 h-6" />
        </button>

        {!showInstructions ? (
          <>
            {/* Header */}
            <div className="text-center mb-6">
              <div className="w-16 h-16 bg-gradient-to-br from-primary to-accent rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg">
                <span className="text-white font-bold text-xl">ES</span>
              </div>
              <h2 className="text-2xl font-bold text-foreground">
                Get the ExpatsSphere App
              </h2>
            </div>

            {/* Why PWA instead of App Store */}
            <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 mb-6">
              <p className="text-sm text-blue-800">
                <strong>Why install as a web app?</strong><br />
                Our web app gives you the full native experience with push notifications, offline access, and instant updates — no app store download needed.
              </p>
            </div>

            {/* Benefits highlight */}
            <div className="bg-green-50 border border-green-200 rounded-xl p-4 mb-6">
              <p className="text-sm text-green-800">
                <strong>Free to join and use!</strong><br />
                Connect with expats, discover events, and build your community.
              </p>
            </div>

            {/* Benefits */}
            <div className="space-y-3 mb-6">
              <div className="flex items-center gap-3">
                <div className="w-6 h-6 bg-green-100 rounded-full flex items-center justify-center">
                  <Check className="w-4 h-4 text-green-600" />
                </div>
                <span className="text-sm text-foreground">Full features & push notifications</span>
              </div>
              <div className="flex items-center gap-3">
                <div className="w-6 h-6 bg-green-100 rounded-full flex items-center justify-center">
                  <Check className="w-4 h-4 text-green-600" />
                </div>
                <span className="text-sm text-foreground">No Apple markup — affordable for all expats</span>
              </div>
              <div className="flex items-center gap-3">
                <div className="w-6 h-6 bg-green-100 rounded-full flex items-center justify-center">
                  <Check className="w-4 h-4 text-green-600" />
                </div>
                <span className="text-sm text-foreground">Install in 30 seconds — no App Store needed</span>
              </div>
            </div>

            {/* CTA Buttons */}
            <div className="space-y-3">
              <Button 
                onClick={handleShowInstructions}
                className="w-full h-12 rounded-full font-bold text-base"
              >
                Show Me How to Install
              </Button>
              <button
                onClick={handleDismiss}
                className="w-full text-sm text-muted-foreground hover:text-foreground transition-colors"
              >
                Maybe Later
              </button>
            </div>
          </>
        ) : (
          <>
            {/* Instructions View */}
            <div className="text-center mb-6">
              <h2 className="text-2xl font-bold text-foreground">
                Install in 3 Easy Steps
              </h2>
              <p className="text-sm text-muted-foreground mt-1">
                Takes about 30 seconds
              </p>
            </div>

            {/* Step by step */}
            <div className="space-y-6 mb-8">
              {/* Step 1 */}
              <div className="flex gap-4">
                <div className="w-10 h-10 bg-primary text-white rounded-full flex items-center justify-center font-bold shrink-0">
                  1
                </div>
                <div>
                  <p className="font-semibold text-foreground">Tap the Share button</p>
                  <p className="text-sm text-muted-foreground">
                    Look for the <Share className="w-4 h-4 inline mx-1" /> icon at the bottom of Safari
                  </p>
                </div>
              </div>

              {/* Step 2 */}
              <div className="flex gap-4">
                <div className="w-10 h-10 bg-primary text-white rounded-full flex items-center justify-center font-bold shrink-0">
                  2
                </div>
                <div>
                  <p className="font-semibold text-foreground">Tap "Add to Home Screen"</p>
                  <p className="text-sm text-muted-foreground">
                    Scroll down in the share menu to find it <Plus className="w-4 h-4 inline mx-1" />
                  </p>
                </div>
              </div>

              {/* Step 3 */}
              <div className="flex gap-4">
                <div className="w-10 h-10 bg-primary text-white rounded-full flex items-center justify-center font-bold shrink-0">
                  3
                </div>
                <div>
                  <p className="font-semibold text-foreground">Tap "Add"</p>
                  <p className="text-sm text-muted-foreground">
                    In the top right corner — and you're done!
                  </p>
                </div>
              </div>
            </div>

            {/* Visual hint */}
            <div className="bg-gray-50 rounded-xl p-4 mb-6 text-center">
              <p className="text-sm text-muted-foreground">
                After installing, ExpatsSphere will appear on your home screen just like any other app!
              </p>
            </div>

            {/* Back button */}
            <Button 
              onClick={handleDismiss}
              className="w-full h-12 rounded-full font-bold text-base"
            >
              Got It!
            </Button>
          </>
        )}
      </div>
    </div>
  );
};

export default IOSInstallPopup;
